import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { FormHandles } from '@unform/core'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import BadgeIsValid from '~/components/BadgeIsValid'
import { Button } from '~/components/Buttons'
import EmptyPage from '~/components/empty-page'
import { FilterSearch } from '~/components/FilterSearch'
import Loading from '~/components/Loading'
import Panel from '~/components/Panel'
import { FormGroup, Label, Textarea } from '~/components/unform'
import { useAppInfo } from '~/hooks/useAppInfo'
import * as types from '~/modules/retailMedia/store/product/constants'
import { useAppSelector } from '~/store/hooks'
import { setQuery } from '~/store/modules/filters/actions'

import { SubpublisherCatalogFilter } from '~/modules/retailMedia/components/SubpublisherCatalogFilter'
import { useSubpublisherOptions } from '~/modules/retailMedia/components/ProductsPanel/useProductsPanelSubpublisher'

import { deleteAllProductsOnCampaign } from '../../store/campaign/actions'
import AlertBrands from '../AlertBrands'
import Product from '../Product'
import ProductsPanelTabs from '../ProductsPanelTabs'

import * as S from './styles'
import { useProductsPanelListProducts } from './useProductsPanelListProducts'

const ProductsPanel: React.FC<ProductsPanelProps<FormHandles>> = ({
  formRef,
  isRequired,
  description,
  isEdit = false,
  isCollapsible = true,
  Buttons,
  currentProducts = [],
  publisherId,
  hideBrandsPanel = false,
  campaignTypeOverride,
  destinationPublisherIdsOverride,
  initialCollapsibleState
}) => {
  const { isWhiteLabel } = useAppInfo()

  // Catálogo de subpublisher de rede / publisher onsite para offsite
  const {
    allSubpublishers,
    selectedSubpublisherCatalog,
    setSelectedSubpublisherCatalog,
    subPublishersCatalogOptions,
    subPublisherIds,
    shouldShowSubpublishersCatalogFilter,
    campaignType
  } = useSubpublisherOptions({
    formRef,
    campaignTypeOverride,
    destinationPublisherIdsOverride
  })

  /**
   * Lógica para a funcionalidade principal
   */

  const [bulkProducts, setBulkProducts] = useState<string>('')
  const [activeTab, setActiveTab] = useState<SearchProductBy>('search')

  const { query } = useAppSelector(state => state.filters)
  const filteredQuery = useMemo(
    () => query?.[`${types.PRODUCT_REDUX_QUERY}`] || null,
    [query]
  )

  // Busca de produto
  const dispatch = useDispatch()

  // Hook para listar produtos pesquisados
  const { handleAddProductsBulk } = useProductsPanelListProducts({
    filteredQuery,
    publisherId,
    subPublisherIds,
    isWhiteLabel,
    activeTab,
    campaignType,
    bulkProducts,
    isEdit,
    formRef
  })

  const {
    campaign: {
      products: {
        selectedProducts: newProducts,
        loadingAddBulk,
        rejectedProducts = null
      }
    },
    product: {
      list: { data: searchProducts, loading: listProductsLoading }
    }
  } = useAppSelector(state => state.retailMedia)

  const allProducts = useMemo(
    () => [...currentProducts, ...newProducts],
    [currentProducts, newProducts]
  )

  const selectedSKUS = useMemo(
    () => allProducts.map(item => item.product_sku),
    [allProducts]
  )
  const selectedIDs = useMemo(
    () => allProducts.map(item => item.id),
    [allProducts]
  )
  const itemsDisableds = useMemo(
    () => allProducts.filter(item => item.item_disabled),
    [allProducts]
  )

  const resetQuery = useCallback(() => {
    dispatch(
      setQuery({
        query: { [types.PRODUCT_REDUX_QUERY]: null },
        keyState: types.PRODUCT_REDUX_QUERY
      })
    )
  }, [dispatch])

  const resetFieldQuery = useCallback(() => {
    resetQuery()
    dispatch(deleteAllProductsOnCampaign(isEdit))
  }, [resetQuery, isEdit])

  useEffect(() => {
    resetFieldQuery()
  }, [resetFieldQuery])

  const handleDeleteAllProducts = useCallback(
    () => dispatch(deleteAllProductsOnCampaign(isEdit)),
    [dispatch, isEdit]
  )

  const handleChangeProductsBulk = useCallback((e: OnChangeTextAreaData) => {
    const data = e.target.value

    setBulkProducts(data)
  }, [])

  const iconLeft = useMemo(
    () => <BadgeIsValid isValid={!!allProducts?.length} />,
    [allProducts]
  )

  const handleTabs = useCallback(
    (tabKey: SearchProductBy) => setActiveTab(tabKey),
    []
  )

  return (
    <Panel
      title={t('rm:ProductsPanel.title')}
      description={description}
      isCollapsible={isCollapsible}
      initialCollapsibleState={initialCollapsibleState}
      {...(isRequired ? { iconLeft } : {})}
    >
      {!hideBrandsPanel && (
        <S.Header>
          <AlertBrands publisherId={publisherId} />
        </S.Header>
      )}

      {/* Lista de pesquisa */}
      <S.ProductsLists>
        <S.ProductsList>
          <S.ProductsListHeader>
            <ProductsPanelTabs
              activeTab={activeTab}
              publisherId={publisherId}
              onChange={handleTabs}
              selectedSubpublisherCatalog={selectedSubpublisherCatalog}
              campaignType={campaignType}
            />

            {activeTab === 'search' && (
              <FilterSearch
                placeholder={t('rm:filter.searchProductsPlaceholder')}
                keyState={`${types.PRODUCT_REDUX_QUERY}`}
                live
                isDebounce
                disabled={!selectedSubpublisherCatalog}
              />
            )}

            {!selectedSubpublisherCatalog && (
              <Alert variant="warning" style={{ marginTop: 8 }}>
                {t('rm:ProductsPanel.alert.selectSubpublisherCatalog')}
              </Alert>
            )}

            {shouldShowSubpublishersCatalogFilter && (
              <SubpublisherCatalogFilter
                key={selectedSubpublisherCatalog}
                selectedSubpublishers={subPublishersCatalogOptions}
                selected={selectedSubpublisherCatalog}
                setSelected={setSelectedSubpublisherCatalog}
                resetQuery={resetQuery}
                allSubpublishers={allSubpublishers}
                isLoading={listProductsLoading}
                activeTab={activeTab}
                handleTabs={handleTabs}
                campaignType={campaignType}
              />
            )}

            <Loading status={listProductsLoading}>
              {t('rm:ProductsPanel.loadingProducts')}...
            </Loading>

            {/* Esta seção permitirá o usuário adicionar vários produtos de uma vez */}
            {activeTab !== 'search' && (
              <S.BulkSection>
                <Loading status={loadingAddBulk}>
                  {t('rm:ProductsPanel.addingProducts')}...
                </Loading>

                {!!rejectedProducts?.length && (
                  <div className="alertsForm" key={`errorOn_${activeTab}`}>
                    <Alert variant="warning">
                      {t('rm:ProductsPanel.alert.addProductError', {
                        codes: rejectedProducts.join(', ') as string
                      })}
                    </Alert>
                  </div>
                )}

                <FormGroup>
                  <Label htmlFor="bulkProducts" isRequired>
                    {t('rm:ProductsPanel.bulkProducts.label')}
                  </Label>
                  <Textarea
                    name="bulkProducts"
                    // eslint-disable-next-line max-len
                    placeholder={t('rm:ProductsPanel.bulkProducts.placeholder')}
                    onChange={handleChangeProductsBulk}
                    disabled={!selectedSubpublisherCatalog}
                  />
                </FormGroup>

                <FormGroup className="actionButtons">
                  <Button
                    text={t('common:actions.add')}
                    onClick={handleAddProductsBulk}
                    disabled={!bulkProducts}
                  />
                </FormGroup>
              </S.BulkSection>
            )}
          </S.ProductsListHeader>

          {!listProductsLoading && activeTab === 'search' && filteredQuery && (
            <div className="d-flex flex-column gap-1">
              {searchProducts?.map(item => (
                <Product
                  campaignType={campaignType}
                  hasAction
                  key={item.id}
                  data={item}
                  selectedSKUS={selectedSKUS}
                  selectedIDs={selectedIDs}
                  itemsDisableds={itemsDisableds}
                  allSubpublishers={allSubpublishers}
                  subPublisherIds={subPublisherIds}
                />
              ))}
            </div>
          )}
        </S.ProductsList>

        {/* Lista de selecionadas */}
        <S.ProductsList>
          <S.ProductsListHeader>
            <S.Heading>
              <S.Title>
                <Trans
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  i18nKey="rm:ProductsPanel.selectedProductsLength"
                  defaults="Produtos selecionados <span>({{count}} produtos)</span>"
                  components={{ span: <span /> }}
                  values={{ count: allProducts.length || 0 }}
                />
              </S.Title>

              {!!allProducts?.length && (
                <Button
                  template="transparent"
                  text={t('c:actions.removeAll')}
                  onClick={handleDeleteAllProducts}
                  size="xsmall"
                />
              )}
            </S.Heading>

            {isEdit && (
              <S.Info>
                <Alert variant="informational">
                  {t('rm:ProductsPanel.alert.cpcOnEditing')}
                </Alert>
              </S.Info>
            )}
          </S.ProductsListHeader>

          <div>
            {allProducts?.map(item => (
              <Product
                hasAction
                key={item?.id}
                data={item}
                isSelected
                allSubpublishers={allSubpublishers}
                campaignType={campaignType}
              />
            ))}
          </div>

          {allProducts.length === 0 && (
            <div className="empty">
              <EmptyPage
                title={t('rm:ProductsPanel.empty.title')}
                subtitle={t('rm:ProductsPanel.empty.subtitle')}
                svgPath="photos/orders-empty"
              />
            </div>
          )}
        </S.ProductsList>
      </S.ProductsLists>

      {Buttons && Buttons}
    </Panel>
  )
}

export default ProductsPanel
